import React, { useCallback, useRef } from 'react'
import { Icon } from '@pff-consumer/web-ui/components/icon/icon'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { SVGGroup } from '@pff-consumer/utils'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/text'
import { colors } from '@pff-consumer/core-ui'
import { useOutsideClick } from '@pff-consumer/tweed-ui/hooks/use-outside-click.web'
import { useEscapeKey } from '@pff-consumer/tweed-ui/hooks/use-escape-key.web'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'

import { useStyles } from './experiment-modal.styles'
import { ExperimentCarousel } from './experiment-carousel/experiment-carousel'
import { sendCloseEvent, sendLinkClickedEvent } from '../../../utils/util-functions'
import { useUpsellModalQueryState } from '../../../hooks/useUpsellModalQueryState'

const { useBreakpoints } = checkoutHooks

const ctaButtonText = 'Subscribe Now'

export const ExperimentModal = () => {
  const modalRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useBreakpoints()
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const { parent, experiment, location } = useUpsellModalQueryState()
  const { variant, images } = experiment
  const styles = useStyles({ isMobile })
  const url = new URL(parent)

  const onClose = () => sendCloseEvent(url.origin)

  useOutsideClick(onClose, modalRef)
  useEscapeKey(onClose)

  const handleSwipe = useCallback(
    (cardSwiped: number, swipeDirection: string) => {
      sendSubscribeAnalyticsEvent('Card Swiped', {
        location: location!,
        variant: `Upsell Modal - ${variant || 'Control'}`,
        cardSwiped,
        swipeDirection,
      })
    },
    [sendSubscribeAnalyticsEvent, variant, location]
  )

  const handleCtaClick = useCallback(() => {
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: ctaButtonText,
      ctaLocation: 'MDS Upsell Modal',
      variant: `${variant || 'Control'}`,
      path: url.pathname,
    })
    sendLinkClickedEvent(url.origin)
  }, [sendSubscribeAnalyticsEvent, url.origin, url.pathname, variant])

  const handleItemClick = useCallback(
    (cardClicked: number) => {
      sendSubscribeAnalyticsEvent('CTA Clicked', {
        ctaName: ctaButtonText,
        ctaLocation: 'MDS Upsell Modal',
        variant: `${variant || 'Control'}`,
        path: url.pathname,
        cardClicked,
      })
      sendLinkClickedEvent(url.origin)
    },
    [sendSubscribeAnalyticsEvent, url.origin, url.pathname, variant]
  )

  return (
    <div
      data-testid='experimentModal'
      className={styles.containerOverlay}
    >
      <div
        className={styles.drawer}
        ref={modalRef}
      >
        <button
          onClick={onClose}
          className={styles.closeButton}
          type='button'
        >
          <Icon
            name='close'
            testID='experimentModal.closeIcon'
            group={SVGGroup.Icons}
            width={isMobile ? 22 : 24}
            height={isMobile ? 22 : 24}
            className={styles.closeIcon}
          />
        </button>

        <ExperimentCarousel
          images={images}
          onCardChange={handleSwipe}
          onItemClick={handleItemClick}
        />

        <button
          type='button'
          data-testid='experimentModal.ctaButton'
          className={styles.ctaButton}
          onClick={handleCtaClick}
        >
          <Text
            variant={TextDescriptionVariant.TEXT_MD_XBOLD}
            colorOverride={colors.alternateButtonTextColor}
          >
            {ctaButtonText}
          </Text>
        </button>
      </div>
    </div>
  )
}
